// «Обучение»: what each lesson teaches. Keys must match services/onboarding.js
// (test/onboarding.test.js checks). target — the button the coach mark points
// at on the real screen: { text: [starts with…] } | { aria: [label…] } | { selector }.
import TodayIcon from '@mui/icons-material/Today';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import MicExternalOnOutlinedIcon from '@mui/icons-material/MicExternalOnOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';

export const CHAPTERS = [
  {
    key: 'day',
    ru: 'Твой день',
    en: 'Your day',
    sub: {
      ru: 'Уроки по времени, что сдать и что поручено — всё, что нужно утром.',
      en: 'Lessons by the clock, what is due and what you were asked to do.',
    },
  },
  {
    key: 'cherry',
    ru: 'Черри — ваш помощник',
    en: 'Cherry, your assistant',
    sub: {
      ru: 'Пишите, говорите голосом или в Telegram — Черри знает ваших детей и план.',
      en: 'Type, talk or use Telegram — Cherry knows your children and the plan.',
    },
  },
  {
    key: 'create',
    ru: 'Твори',
    en: 'Create',
    sub: {
      ru: 'Песни, караоке, рабочие листы, картинки, наклейки — за минуту и для своей группы.',
      en: 'Songs, karaoke, worksheets, pictures, stickers — in a minute, for your group.',
    },
  },
  {
    key: 'kids',
    ru: 'Дети и родители',
    en: 'Children and parents',
    sub: {
      ru: 'Оценки, видео, домашние задания и отчёты, которые читают родители.',
      en: 'Assessments, videos, homework and the reports parents read.',
    },
  },
];

export const LESSONS = [
  // ── Твой день ──
  {
    key: 'today',
    chapter: 'day',
    Icon: TodayIcon,
    minutes: 2,
    demo: 'day',
    title: {
      ru: '«Сегодня»: ваш день по минутам',
      en: 'Today: your day by the minute',
    },
    why: {
      ru: 'Все уроки дня по времени: что идёт сейчас, что дальше, тема, слова и материалы. После урока отметьте его — запись «что сделали дети» попадёт в отчёты родителям.',
      en: 'Every lesson of the day by time: what is on now, what is next, topic, words and materials. Mark a lesson after it — the note of what the children made goes into the parents’ reports.',
    },
    steps: [
      {
        ru: 'Откройте «Сегодня» и нажмите на урок.',
        en: 'Open Today and tap a lesson.',
      },
      { ru: 'Включите «Урок проведён».', en: 'Switch on “Lesson done”.' },
      {
        ru: 'Напишите, что сделали дети, и нажмите «Сохранить запись».',
        en: 'Write what the children made and press “Save note”.',
      },
    ],
    auto: {
      ru: 'Отметится само, когда вы отметите первый урок.',
      en: 'Ticks itself when you mark your first lesson.',
    },
    try: {
      path: '/today',
      target: {
        selector: '[data-reveal] [aria-expanded]',
        text: ['Добавить урок', 'Add a lesson'],
      },
    },
  },
  {
    key: 'calendar',
    chapter: 'day',
    Icon: EventNoteOutlinedIcon,
    minutes: 1,
    demo: 'calendar',
    title: {
      ru: '«Календарь»: что сдать и когда',
      en: 'Calendar: what is due and when',
    },
    why: {
      ru: 'Домашние задания, итоги недели и планы на месяц — по дням. Черновик делается одной кнопкой, готовое и отправленное видно сразу. Дни с наборами к урокам тоже отмечены.',
      en: 'Homework, weekly summaries and month plans by day. A draft is one button away; made and sent things show at once. Days with lesson sets are marked too.',
    },
    steps: [
      { ru: 'Откройте «Календарь».', en: 'Open Calendar.' },
      {
        ru: 'Нажмите на день — справа то, что нужно сдать.',
        en: 'Tap a day — what is due shows on the right.',
      },
      {
        ru: '«Сгенерировать» сделает черновик — проверьте и отправьте.',
        en: '“Generate” makes a draft — check it and send.',
      },
    ],
    auto: {
      ru: 'Отметится, когда вы откроете календарь.',
      en: 'Ticks when you open the calendar.',
    },
    try: { path: '/calendar', target: { text: ['Сегодня', 'Today'] } },
  },
  {
    key: 'tasks',
    chapter: 'day',
    Icon: ChecklistIcon,
    minutes: 1,
    demo: 'tasks',
    title: {
      ru: '«Задачи»: что поручено вам',
      en: 'Tasks: what you were asked to do',
    },
    why: {
      ru: 'Задачи от администрации и свои — со сроками. Просроченные видны красной цифрой в меню, выполненные уходят на проверку.',
      en: 'Tasks from the office and your own, with deadlines. Overdue ones show as a red number in the menu.',
    },
    steps: [
      { ru: 'Откройте «Задачи».', en: 'Open Tasks.' },
      {
        ru: 'Доска: «Думаю» → «Делаю» → «Готово» — перетаскивайте задачи пальцем или мышкой.',
        en: 'The board: Thinking → Doing → Done — drag tasks with a finger or the mouse.',
      },
      { ru: '«+» — добавить свою задачу.', en: '“+” adds your own task.' },
    ],
    auto: {
      ru: 'Отметится, когда вы откроете задачи.',
      en: 'Ticks when you open Tasks.',
    },
    try: { path: '/tasks', target: { aria: ['Добавить', 'Add'] } },
  },

  // ── Черри ──
  {
    key: 'assistant',
    chapter: 'cherry',
    Icon: SmartToyIcon,
    minutes: 3,
    demo: 'chat',
    title: { ru: 'Спросите Черри', en: 'Ask Cherry' },
    why: {
      ru: 'Черри — помощник, который знает план, ваши группы и детей. Попросите план урока, домашнее задание, письмо родителям, игру на 5 минут. Можно приложить фото или PDF.',
      en: 'Cherry knows the plan, your groups and children. Ask for a lesson plan, homework, a note to parents, a 5-minute game. You can attach a photo or PDF.',
    },
    steps: [
      {
        ru: 'Откройте «AI-помощник» — вопрос уже написан за вас.',
        en: 'Open AI Assistant — the question is already typed for you.',
      },
      {
        ru: 'Поменяйте тему или возраст, если хотите.',
        en: 'Change the topic or age if you like.',
      },
      {
        ru: 'Нажмите «Отправить» и посмотрите, что предложит Черри.',
        en: 'Press Send and see what Cherry suggests.',
      },
    ],
    auto: {
      ru: 'Отметится после первого разговора с Черри.',
      en: 'Ticks after your first chat with Cherry.',
    },
    try: {
      path: '/assistant',
      prompt: {
        ru: 'Составь план урока на 30 минут по теме «Цвета» для детей 4–5 лет: разминка, песенка, игра с движениями и закрепление.',
        en: 'Make a 30-minute lesson plan on “Colours” for children aged 4–5: warm-up, a song, a movement game and a wrap-up.',
      },
      target: { selector: 'textarea:not([aria-hidden])' },
    },
  },
  {
    key: 'talk',
    chapter: 'cherry',
    Icon: RecordVoiceOverRoundedIcon,
    minutes: 2,
    demo: 'voice',
    title: { ru: 'Разговор с Черри голосом', en: 'Talk to Cherry by voice' },
    why: {
      ru: 'Когда руки заняты — просто говорите. Черри отвечает голосом и улыбается. В обычном чате есть микрофон для диктовки (на компьютере — правый Ctrl).',
      en: 'Hands busy? Just talk. Cherry answers out loud. The chat also has a microphone for dictation (right Ctrl on a computer).',
    },
    steps: [
      { ru: 'Откройте «Разговор с Черри».', en: 'Open Talk to Cherry.' },
      {
        ru: 'Удерживайте круглую кнопку и говорите.',
        en: 'Hold the round button and speak.',
      },
      { ru: 'Отпустите — Черри ответит.', en: 'Let go — Cherry answers.' },
    ],
    auto: {
      ru: 'Отметится, когда вы откроете разговор.',
      en: 'Ticks when you open Talk.',
    },
    try: {
      path: '/talk',
      target: { aria: ['Удерживайте, чтобы говорить', 'Hold to talk'] },
    },
  },
  {
    key: 'telegram',
    chapter: 'cherry',
    Icon: TelegramIcon,
    minutes: 2,
    demo: 'phone',
    title: { ru: 'Черри в вашем Telegram', en: 'Cherry in your Telegram' },
    why: {
      ru: 'Привяжите Telegram — и пишите Черри прямо оттуда, хоть голосовым. Карточки (песни, документы) приходят кнопками.',
      en: 'Link Telegram and write to Cherry from there, even with voice notes. Songs and documents arrive as buttons.',
    },
    steps: [
      { ru: 'Нажмите «Привязать».', en: 'Press «Привязать» (Link).' },
      {
        ru: '«Открыть бота и нажать „Старт“» — в Telegram нажмите «Старт».',
        en: 'Open the bot and press Start in Telegram.',
      },
      {
        ru: 'Напишите боту «Привет» — ответит Черри.',
        en: 'Say “Hi” to the bot — Cherry answers.',
      },
    ],
    auto: {
      ru: 'Отметится, когда Telegram будет привязан.',
      en: 'Ticks once Telegram is linked.',
    },
    try: { action: 'telegram' },
  },
  {
    key: 'memory',
    chapter: 'cherry',
    Icon: PsychologyOutlinedIcon,
    minutes: 2,
    demo: 'memory',
    title: {
      ru: '«Память»: что важно помнить о детях',
      en: 'Memory: what to remember about the children',
    },
    why: {
      ru: 'Запишите один раз: «Ева болела с 12 по 16 октября», «Тимур стесняется отвечать при всех». Отчёты и Черри будут это учитывать, а даты болезни — в посещаемости.',
      en: 'Write it once: “Eva was ill 12–16 October”, “Tim is shy answering in front of everyone”. Reports and Cherry take it into account.',
    },
    steps: [
      { ru: 'Откройте «Память».', en: 'Open Memory.' },
      {
        ru: 'Нажмите «Добавить о ребёнке…», выберите ребёнка.',
        en: 'Press “Add about a child…” and pick the child.',
      },
      {
        ru: 'Напишите факт и, если нужно, даты.',
        en: 'Write the fact and dates if needed.',
      },
    ],
    auto: {
      ru: 'Отметится после первой записи.',
      en: 'Ticks after your first note.',
    },
    try: {
      path: '/memory',
      target: { text: ['Добавить о ребёнке', 'Add about a child'] },
    },
  },

  // ── Твори ──
  {
    key: 'karaoke',
    chapter: 'create',
    Icon: MicExternalOnOutlinedIcon,
    minutes: 1,
    demo: 'karaoke',
    example: 'song',
    free: true,
    title: { ru: 'Караоке на весь экран', en: 'Full-screen karaoke' },
    why: {
      ru: 'Песня с подсветкой слов — дети поют за экраном. Можно повторять строку, замедлить темп, сделать текст крупнее. Попробуйте прямо здесь на песне школы — это бесплатно.',
      en: 'Words light up as they are sung — children sing along. Repeat a line, slow it down, make the text bigger. Try it here on a school song — it is free.',
    },
    steps: [
      {
        ru: 'Нажмите ▶ у песни ниже — слова побегут под музыку.',
        en: 'Press ▶ on the song below — the words follow the music.',
      },
      {
        ru: 'Нажмите «Караоке» — откроется весь экран.',
        en: 'Press Karaoke — it goes full screen.',
      },
      {
        ru: 'Нажмите на строку — песня перейдёт к ней.',
        en: 'Tap a line — the song jumps there.',
      },
    ],
    auto: {
      ru: 'Отметится, когда вы откроете караоке.',
      en: 'Ticks when you open karaoke.',
    },
    try: { path: '/songs', target: { text: ['Караоке'], aria: ['Караоке'] } },
  },
  {
    key: 'song',
    chapter: 'create',
    Icon: LibraryMusicOutlinedIcon,
    minutes: 3,
    demo: 'song',
    cost: {
      ru: '≈ 12 кредитов Suno (2 версии)',
      en: '≈ 12 Suno credits (2 versions)',
    },
    title: {
      ru: 'Песня из стишка за минуту',
      en: 'A song from a rhyme in a minute',
    },
    why: {
      ru: 'Вставьте стишок или слова урока — получится настоящая песня с музыкой и пением, две версии. Нет слов? Нажмите «Сочинить слова с Черри».',
      en: 'Paste a rhyme or the lesson words — you get a real song with music and singing, two versions. No words? Press “Write words with Cherry”.',
    },
    steps: [
      {
        ru: 'Откройте «Песни» и вставьте слова (или «Сочинить слова с Черри»).',
        en: 'Open Songs and paste the words (or “Write words with Cherry”).',
      },
      { ru: 'Выберите музыку и голос.', en: 'Pick the music and the voice.' },
      {
        ru: 'Нажмите «Сделать песню» — через 30–90 секунд она готова.',
        en: 'Press “Make song” — ready in 30–90 seconds.',
      },
    ],
    auto: {
      ru: 'Отметится, когда будет ваша первая песня.',
      en: 'Ticks with your first song.',
    },
    try: { path: '/songs', target: { text: ['Сделать песню'] } },
  },
  {
    key: 'worksheet',
    chapter: 'create',
    Icon: DrawOutlinedIcon,
    minutes: 3,
    demo: 'sheet',
    example: 'worksheet',
    cost: { ru: '≈ $0,15 за лист', en: '≈ $0.15 a sheet' },
    title: {
      ru: 'Рабочий лист по теме или по фото',
      en: 'A worksheet from a topic or a photo',
    },
    why: {
      ru: 'Черри рисует рабочий лист (A4, PDF) по теме урока — или по фото страницы учебника, но новый. Готовый лист сразу в библиотеке, его можно положить в набор к уроку.',
      en: 'Cherry draws an A4 worksheet (PDF) for a topic — or a fresh one from a photo of a textbook page. It lands in the library and can go into a lesson set.',
    },
    steps: [
      {
        ru: 'Откройте Черри — просьба уже написана.',
        en: 'Open Cherry — the request is already typed.',
      },
      {
        ru: 'Хотите по учебнику? Приложите фото страницы (скрепка).',
        en: 'From a textbook? Attach a photo of the page (paper clip).',
      },
      {
        ru: 'Отправьте — через минуту будет PDF.',
        en: 'Send — the PDF is ready in about a minute.',
      },
    ],
    auto: {
      ru: 'Отметится, когда Черри сделает ваш первый лист.',
      en: 'Ticks with your first worksheet.',
    },
    try: {
      path: '/assistant',
      prompt: {
        ru: 'Сделай рабочий лист для детей 4–5 лет: обведи по точкам и раскрась фигуры — circle, square, triangle, star.',
        en: 'Make a worksheet for children aged 4–5: trace the dotted lines and colour the shapes — circle, square, triangle, star.',
      },
      target: { selector: 'textarea:not([aria-hidden])' },
    },
  },
  {
    key: 'picture',
    chapter: 'create',
    Icon: CollectionsOutlinedIcon,
    minutes: 2,
    demo: 'pictures',
    cost: { ru: '≈ $0,10 за картинку', en: '≈ $0.10 a picture' },
    title: { ru: 'Картинки для урока', en: 'Pictures for the lesson' },
    why: {
      ru: 'Карточки без текста для игры «найди пару», сцена для рассказа, картинка по образцу. Всё сохраняется в «Картинках», оттуда — в PDF.',
      en: 'Text-free cards for a matching game, a scene for a story, a picture from a sample. Everything is kept in Pictures and turns into a PDF.',
    },
    steps: [
      { ru: 'Откройте «Картинки».', en: 'Open Pictures.' },
      {
        ru: 'Опишите, что нарисовать (можно приложить образец).',
        en: 'Describe what to draw (a sample is optional).',
      },
      { ru: 'Нажмите «Нарисовать».', en: 'Press “Draw”.' },
    ],
    auto: {
      ru: 'Отметится с первой картинкой.',
      en: 'Ticks with your first picture.',
    },
    try: { path: '/images', target: { text: ['Нарисовать'] } },
  },
  {
    key: 'stickers',
    chapter: 'create',
    Icon: FaceRetouchingNaturalOutlinedIcon,
    minutes: 2,
    demo: 'stickers',
    cost: {
      ru: '≈ $0,10 за человека (один раз)',
      en: '≈ $0.10 per person (once)',
    },
    title: {
      ru: 'Наклейки с лицами детей',
      en: 'Stickers with the children’s faces',
    },
    why: {
      ru: 'Из фото ребёнка — лист наклеек в мультяшном стиле. Дети в восторге: награды, именные карточки, «кто сегодня дежурный».',
      en: 'From a child’s photo — a sheet of cartoon stickers. Children love them: rewards, name cards, “today’s helper”.',
    },
    steps: [
      {
        ru: 'Откройте «Картинки» → «Наклейки с лицами».',
        en: 'Open Pictures → “Stickers with faces”.',
      },
      {
        ru: 'Отметьте детей (нужны их фото в профиле).',
        en: 'Tick the children (their photos must be in the profile).',
      },
      {
        ru: 'Соберите лист и распечатайте.',
        en: 'Build the sheet and print it.',
      },
    ],
    auto: {
      ru: 'Отметится с первыми наклейками.',
      en: 'Ticks with your first stickers.',
    },
    try: { path: '/images', target: { text: ['Наклейки с лицами'] } },
  },
  {
    key: 'kit',
    chapter: 'create',
    Icon: Inventory2OutlinedIcon,
    minutes: 2,
    demo: 'kit',
    free: true,
    title: {
      ru: 'Набор к уроку: песня + стих + PDF',
      en: 'A lesson set: song + poem + PDF',
    },
    why: {
      ru: 'Соберите всё для урока в один набор и поставьте его на дату — в «Сегодня» он откроется прямо в карточке урока: песня с караоке, стих крупно, рабочий лист.',
      en: 'Put everything for a lesson into one set and place it on a date — Today opens it right in the lesson card: song with karaoke, poem in big letters, worksheet.',
    },
    steps: [
      {
        ru: 'В «Сегодня» откройте урок → «Песня, стих, PDF к уроку» (или «В набор» у песни).',
        en: 'In Today open a lesson → “Song, poem, PDF for this lesson” (or “В набор” on a song).',
      },
      {
        ru: 'Назовите набор и добавьте стих, песню, PDF.',
        en: 'Name the set and add a poem, a song, a PDF.',
      },
      {
        ru: 'Готово — набор ждёт вас в карточке урока.',
        en: 'Done — the set waits in the lesson card.',
      },
    ],
    auto: {
      ru: 'Отметится, когда набор будет стоять на дате.',
      en: 'Ticks when a set is on a date.',
    },
    try: {
      path: '/today',
      target: { text: ['Набор на весь день', 'Set for the whole day'] },
    },
  },
  {
    key: 'document',
    chapter: 'create',
    Icon: ArticleOutlinedIcon,
    minutes: 2,
    demo: 'doc',
    free: true,
    title: {
      ru: '«Документы» в стиле школы',
      en: 'Documents in the school style',
    },
    why: {
      ru: 'Домашка, отчёт, план — красивым PDF в стиле Cherry-Pick. Черри напишет черновик, вы поправите. Ссылка для родителей и отправка в Telegram — в один клик.',
      en: 'Homework, a report, a plan — as a neat PDF in the school style. Cherry drafts it, you edit. A link for parents or Telegram in one click.',
    },
    steps: [
      {
        ru: 'Откройте «Документы» → «Новый документ».',
        en: 'Open Documents → “New document”.',
      },
      {
        ru: 'Выберите вид, впишите тему — или попросите Черри.',
        en: 'Pick a kind, type the topic — or ask Cherry.',
      },
      {
        ru: 'Скачайте PDF или поделитесь ссылкой.',
        en: 'Download the PDF or share a link.',
      },
    ],
    auto: {
      ru: 'Отметится с первым документом.',
      en: 'Ticks with your first document.',
    },
    try: {
      path: '/documents',
      target: { text: ['Новый документ', 'New document'] },
    },
  },

  // ── Дети и родители ──
  {
    key: 'assessment',
    chapter: 'kids',
    Icon: AssessmentOutlinedIcon,
    minutes: 3,
    demo: 'assess',
    free: true,
    title: {
      ru: 'Оценка ребёнка по шаблону',
      en: 'Assessing a child with a template',
    },
    why: {
      ru: 'Профиль ребёнка: оценки по темам, прогресс по времени, сравнение, портфолио. Оценка занимает пару минут — потом из неё строятся отчёты.',
      en: 'A child’s profile: scores by theme, progress over time, comparison, portfolio. An assessment takes a couple of minutes — reports are built from it.',
    },
    steps: [
      {
        ru: 'На главной откройте ребёнка из своей группы.',
        en: 'On the home page open a child from your group.',
      },
      {
        ru: 'Нажмите «Новая оценка» и выберите шаблон.',
        en: 'Press “New Assessment” and pick a template.',
      },
      {
        ru: 'Отметьте, что ребёнок уже умеет, и сохраните.',
        en: 'Tick what the child can do and save.',
      },
    ],
    auto: {
      ru: 'Отметится с первой оценкой ваших детей.',
      en: 'Ticks with the first assessment of your children.',
    },
    try: { path: '/' },
  },
  {
    key: 'video',
    chapter: 'kids',
    Icon: VideocamOutlinedIcon,
    minutes: 2,
    demo: 'video',
    free: true,
    title: { ru: 'Видео с оценки', en: 'Video of an assessment' },
    why: {
      ru: 'Запишите, как ребёнок отвечает: в приложении Core для Android — экран, ваше лицо в углу и звук. Видео сохраняется в портфолио, родители видят прогресс своими глазами.',
      en: 'Record the child answering: the Core Android app captures the screen, your face in a corner and the sound. The video goes to the portfolio.',
    },
    steps: [
      {
        ru: 'Откройте ребёнка и его последнюю оценку.',
        en: 'Open the child and their latest assessment.',
      },
      {
        ru: 'В блоке «Видео» нажмите «Записать» (на телефоне — в приложении Core).',
        en: 'In the Video block press Record (on a phone — in the Core app).',
      },
      {
        ru: 'Остановите — видео загрузится само.',
        en: 'Stop — the video uploads by itself.',
      },
    ],
    auto: {
      ru: 'Отметится с первым видео.',
      en: 'Ticks with your first video.',
    },
    try: { path: '/' },
  },
  {
    key: 'report',
    chapter: 'kids',
    Icon: ForumOutlinedIcon,
    minutes: 3,
    demo: 'report',
    title: {
      ru: 'Домашка и отчёты родителям',
      en: 'Homework and reports for parents',
    },
    why: {
      ru: '«AI-генератор» пишет домашнее задание для группы, итог недели и месячный отчёт по каждому ребёнку — из ваших оценок, записей и «Памяти». Вы проверяете и отправляете в Telegram.',
      en: 'The AI Generator writes group homework, the weekly summary and each child’s monthly report — from your assessments, notes and Memory. You check and send to Telegram.',
    },
    steps: [
      { ru: 'Откройте «AI-генератор».', en: 'Open AI Generator.' },
      {
        ru: 'Вкладка «Домашнее задание» — выберите группу и сгенерируйте.',
        en: 'Homework tab — pick a group and generate.',
      },
      {
        ru: 'Проверьте текст и отправьте родителям.',
        en: 'Check the text and send it to parents.',
      },
    ],
    auto: {
      ru: 'Отметится с первой домашкой или отчётом.',
      en: 'Ticks with your first homework or report.',
    },
    try: { path: '/ai', target: { text: ['Домашнее задание'] } },
  },
  {
    key: 'inbox',
    chapter: 'kids',
    Icon: MoveToInboxOutlinedIcon,
    minutes: 1,
    demo: 'inbox',
    free: true,
    title: {
      ru: '«Входящие»: файлы коллегам',
      en: 'Inbox: files for colleagues',
    },
    why: {
      ru: 'Отправьте документы администрации на проверку или коллеге как задание — по ссылке, на несколько часов. Получатель может поправить файлы вместе с Черри.',
      en: 'Send documents to the office for review or to a colleague as a task — by a link for a few hours. They can fix the files together with Cherry.',
    },
    steps: [
      {
        ru: 'Откройте «Входящие» → «Отправить файлы».',
        en: 'Open Inbox → “Send files”.',
      },
      {
        ru: 'Выберите документы и кому.',
        en: 'Choose the documents and who gets them.',
      },
      {
        ru: 'Получатель увидит их во «Входящих».',
        en: 'They will see them in their Inbox.',
      },
    ],
    auto: {
      ru: 'Отметится, когда вы что-то отправите.',
      en: 'Ticks when you send something.',
    },
    try: {
      path: '/inbox',
      target: { text: ['Отправить файлы', 'Send files'] },
    },
  },
];

export const BY_KEY = Object.fromEntries(LESSONS.map((l) => [l.key, l]));

// «?» in the top bar: which lesson explains the screen you are on
export const HELP_BY_PATH = {
  '/today': 'today',
  '/calendar': 'calendar',
  '/tasks': 'tasks',
  '/assistant': 'assistant',
  '/talk': 'talk',
  '/memory': 'memory',
  '/songs': 'song',
  '/images': 'picture',
  '/documents': 'document',
  '/ai': 'report',
  '/inbox': 'inbox',
};

// «Идеи»: ready requests to Черри (they open the chat with the text typed in)
export const IDEAS = [
  {
    ru: 'Сочини весёлый стишок про осень со словами leaf, red, yellow — и сделай из него песню',
    en: 'Write a cheerful autumn rhyme with leaf, red, yellow — and make it a song',
  },
  {
    ru: 'Нарисуй карточки без текста: apple, banana, cherry, lemon, pear, plum — для игры «найди пару»',
    en: 'Draw text-free cards: apple, banana, cherry, lemon, pear, plum — for a matching game',
  },
  {
    ru: 'Придумай игру с движениями на 5 минут, чтобы повторить цвета',
    en: 'Invent a 5-minute movement game to revise colours',
  },
  {
    ru: 'Сделай рабочий лист: соедини животное с его домиком, для детей 5 лет',
    en: 'Make a worksheet: match each animal with its home, for 5-year-olds',
  },
  {
    ru: 'Составь план урока «My family» на 30 минут: песня, игра, поделка',
    en: 'Plan a 30-minute “My family” lesson: a song, a game, a craft',
  },
  {
    ru: 'Напиши родителям короткое тёплое сообщение о том, что мы сегодня учили',
    en: 'Write parents a short warm message about what we learned today',
  },
  {
    ru: 'Я приложу фото страницы учебника — сделай по ней новый рабочий лист в том же духе',
    en: 'I will attach a photo of a textbook page — make a fresh worksheet in the same spirit',
  },
  {
    ru: 'Придумай сказку на 2 минуты, где герои считают до десяти',
    en: 'Invent a 2-minute story where the characters count to ten',
  },
];
